import {Elements} from "@stripe/react-stripe-js";
import {loadStripe} from "@stripe/stripe-js";
import {useMutation, useQuery} from "@tanstack/react-query";
import {createPaymentSheet} from "api";
import {calculateBookingPrice, getUserPendingMethods} from "api/booking.api";
import {getTeacherDetailsById} from "api/teacher.api.ts";
import {useSelectedTimings} from "context/TimingContext";
import {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router-dom";
import styled from "styled-components";
import {PrevButton} from "ui/base/Button/EmblaButtons";
import {mediaSizes, pageContainerStyles, themeColor} from "ui/theme";
import {themeFontSize} from "ui/theme/index";
import {userRoute} from "../user.routes";
import {PaymentCard} from "./sections/PaymentCard";
import {PaymentForm} from "./sections/PaymentForm";

export const PaymentPage = () => {
    const {class_id} = useParams();
    const navigate = useNavigate();

    const {
        selectedTimings: timing,
        numberOfStudents,
        typeOfClass,
    } = useSelectedTimings();

    const [lessonsCost, setLessonsCost] = useState({
        classCost: 0,
        discount: 0,
        tax: 0,
        totalCost: 0,
    });
    const [stripeInstance, setStripeInstance] = useState(null);

    const {data} = useQuery({
        queryKey: ["getTeacherDetailsById", class_id],
        queryFn: () => getTeacherDetailsById({id: class_id}),
        enabled: !!class_id,
    });

    const classDetail = data?.data;
    const teacherId = classDetail?.teacher?.id;

    useQuery({
        queryKey: ["getUserPendingMethods", teacherId],
        queryFn: () => getUserPendingMethods(teacherId),
        enabled: !!teacherId,
        onSuccess: (data) => {
            data.data && navigate(userRoute.classDetail + class_id);
        },
    });

    const {data: paymentSheet} = useQuery({
        queryKey: ["createPaymentSheet"],
        queryFn: () => createPaymentSheet(),
        refetchInterval: false,
        refetchOnMount: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
    });

    const {mutate: getBookingPrice} = useMutation({
        mutationFn: calculateBookingPrice,
        onError: () => useToast("error", "Booking price can not load"),
        onSuccess: ({data}) => {
            setLessonsCost({
                classCost: data.booking_total,
                discount: data.discount_amount,
                tax: data.tax,
                totalCost: data.total_cost,
            });
        },
    });

    useEffect(() => {
        if (!paymentSheet || stripeInstance) return;

        (async () => {
            const stripePromise = await loadStripe(paymentSheet.publishableKey);
            setStripeInstance(stripePromise);
        })();
    }, [paymentSheet]);

    useEffect(() => {
        if (!timing.length) {
            navigate(userRoute.showMoreDates + class_id);
        }

        if (!teacherId) return;

        getBookingPrice({
            online_type: timing[0]?.type?.toLowerCase(),
            class_type: typeOfClass.value.toLowerCase(),
            no_of_class: timing.length,
            no_of_users: numberOfStudents.value,
            teacher_id: teacherId,
        });
    }, [teacherId, timing]);

    const classType = timing?.[0]?.type?.toLowerCase() === "online" ? "online" : "offline";

    return (
        <Container>
            <Header>
                <PrevButton onClick={() => navigate(-1)}/>
                <p>Pay Details</p>
            </Header>
            <ContentWrapper>
                {paymentSheet ? (
                    <Elements
                        stripe={stripeInstance}
                        options={{clientSecret: paymentSheet?.setupIntent}}
                    >
                        <PaymentForm
                            lessonsCostData={lessonsCost}
                            timing={timing}
                            class_id={+class_id}
                            onlineType={classType}
                            subjectToTeach={classDetail?.teacher?.subject_want_to_teach}
                            numberOfStudents={numberOfStudents}
                            haveDiscount={lessonsCost.discount > 0}
                            teacher_id={teacherId}
                            clientSicret={paymentSheet.setup}
                            firstName={classDetail?.first_name}
                            lastName={classDetail?.last_name}
                            cancellation={classDetail?.teacher?.cancellation}
                            typeOfClass={typeOfClass}
                        />
                        <PaymentCard
                            teacherInfo={classDetail?.teacher}
                            lessonsCost={lessonsCost}
                            timing={timing}
                            onlineType={classType}
                            numberOfStudents={numberOfStudents.label}
                            typeOfClass={typeOfClass.label}
                            fullName={
                                classDetail?.first_name + " " + classDetail?.last_name[0]
                            }
                            cancellation={classDetail?.teacher?.cancellation}
                        />
                    </Elements>
                ) : null}
            </ContentWrapper>
        </Container>
    );
};

const Container = styled.div`
    ${pageContainerStyles}
    font-family: var(--font-secondary);
    height: auto;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 40px;
    padding-bottom: 1rem;
    min-height: 83vh;

    @media (min-width: 1300px) {
        min-height: 87vh;
    }
`;

const Header = styled.header`
    display: flex;
    align-items: center;
    gap: 1rem;
    height: fit-content;

    & > p {
        font-size: ${themeFontSize("h3.4")};
        color: ${themeColor("txt")};
        font-weight: 600;
    }
`;

const ContentWrapper = styled.div`
    display: grid;

    gap: 1rem;
    width: 100%;
    height: 100%;

    margin-bottom: 2rem;

    @media (min-width: 1024px) {
        grid-template-columns: 1fr 1fr;
    }
`;
